import { aiTemplateKey, normalizeAISuggestion } from '@/hooks/services/aiGenerationService'
import type { FieldProvenance, GetContentModuleResponse, GetStepsResponse } from '@/services/api/api.types'

type Step = GetStepsResponse[number]

/**
 * Module form fields the backend records provenance for, mapped to the "Ask Sidekick"
 * template that also produces them. `null` marks a field the regeneration rewrites but
 * the portal never asks for directly.
 *
 * `name` and `shortDescription` carry no marker of their own — they are not in the
 * backend's regeneration set, so nothing overwrites them. Their badge falls back to the
 * template comparison below.
 *
 * Written as the backend's own ids; `aiTemplateKey` converts them to the keys the
 * response transform actually delivers.
 */
const MODULE_TEMPLATE_IDS = {
  name: 'generate_cm_title',
  shortDescription: 'generate_cm_description',
  introduction: 'generate_cm_introduction',
  outline: 'generate_cm_outline',
  demographic: 'generate_cm_target_audience',
  presentationStyle: null,
} as const

/** Named as the backend does, so these line up with `field_sources` keys. */
export type ProvenanceModuleField = keyof typeof MODULE_TEMPLATE_IDS
export type ProvenanceStepField = 'name' | 'description'

function moduleMarker(
  cm: Pick<GetContentModuleResponse, 'fieldSources'> | undefined,
  field: ProvenanceModuleField,
): FieldProvenance | undefined {
  // The backend tracks two fields the module form has no input for; narrowing here
  // keeps every caller from having to know which of them it can ask about.
  const sources: Partial<Record<ProvenanceModuleField, FieldProvenance>> = cm?.fieldSources ?? {}
  return sources[field]
}

function matchesGeneratedText(currentValue: string | null | undefined, generated: string | null | undefined) {
  if (!currentValue?.trim() || !generated?.trim()) return false
  return normalizeAISuggestion(currentValue) === normalizeAISuggestion(generated)
}

/**
 * Whether a field still holds the machine's text rather than a person's.
 *
 * Two sources, in order. `field_sources` is the backend's own record and is
 * authoritative where it exists (C2-2597 for the module, C2-2600 for files). Where it
 * doesn't, the value is compared against the last generated text the response carries —
 * the same comparison the backend makes when it writes the marker.
 *
 * No evidence reads as human-written. A wrong badge claims authorship of someone's
 * writing and invites a regeneration to eat it; a missing badge only omits a hint.
 */
export function isModuleFieldGenerated(
  cm: Partial<GetContentModuleResponse> | undefined,
  field: ProvenanceModuleField,
  formValue?: string | null,
): boolean {
  // Provenance describes what is stored, so the saved value is what it classifies.
  const currentValue = cm?.[field]
  if (!cm || typeof currentValue !== 'string' || !currentValue.trim()) return false

  // `undefined` means the caller has no opinion — the module form is uncontrolled and its
  // values arrive a render after the query, so a caller that cannot yet tell passes nothing
  // rather than an empty string, and the marker stands.
  if (formValue === undefined || formValue === null) return matchesMarker(cm, field, currentValue)

  // An empty field has no text to attribute, whatever is stored.
  if (!formValue.trim()) return false

  // An unsaved divergence is something the marker cannot have seen, and the safe reading of
  // that is the expert's — the same rule `selectFieldsToGenerate` applies before it
  // regenerates. In-session typing is caught by the badge's own edit tracking; a restored
  // draft is not, because `setValues` arrives without a keystroke.
  if (formValue.trim() !== currentValue.trim()) return false

  return matchesMarker(cm, field, currentValue)
}

function matchesMarker(
  cm: Partial<GetContentModuleResponse>,
  field: ProvenanceModuleField,
  currentValue: string,
): boolean {
  const marker = moduleMarker(cm, field)
  if (marker) return marker.source === 'generated'

  const templateId = MODULE_TEMPLATE_IDS[field]
  if (!templateId) return false
  return matchesGeneratedText(currentValue, cm.aiGenerateTemplates?.[aiTemplateKey(templateId)]?.result)
}

/**
 * File-level equivalent. The shadow fields are the fallback here rather than the
 * templates: processing writes `name`/`description` and their `*_ai` copies together,
 * so a file that has never been saved through the editor still reports correctly.
 *
 * `currentValue` is the *saved* value, as the module version reads `cm[field]`; classifying
 * the live input resets the badge's local override as the field empties and refills.
 * `displayedValue` is that live input and only disqualifies: the modal fills its fields once
 * and never resyncs, so a divergence is text the marker never saw (SME-1124).
 */
export function isStepFieldGenerated(
  step: Pick<Step, 'fieldSources' | 'titleAi' | 'descriptionAi'> | undefined,
  field: ProvenanceStepField,
  currentValue: string | null | undefined,
  displayedValue?: string | null,
): boolean {
  if (!step || !currentValue?.trim()) return false

  // `undefined`/`null` is a caller with no opinion, and the marker stands. An emptied field
  // needs no case of its own: `currentValue` is non-blank by here, so it diverges.
  if (displayedValue != null && displayedValue.trim() !== currentValue.trim()) return false

  const marker = step.fieldSources?.[field]
  if (marker) return marker.source === 'generated'

  return matchesGeneratedText(currentValue, field === 'name' ? step.titleAi : step.descriptionAi)
}
